import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import contactsService, { Contact, ContactInput } from '../../../services/crm/contactsService';
import companiesService, { Company } from '../../../services/crm/companiesService';
import { useCrmOrgStore } from '../../../store/crmOrg.store';
import EnhancedContactForm from '../../../components/crm/EnhancedContactForm';

type ViewMode = 'table' | 'kanban' | 'gallery';

const LIFECYCLE_OPTIONS = [
  { value: '', label: 'All Stages' },
  { value: 'lead', label: 'Lead' },
  { value: 'mql', label: 'Marketing Qualified' },
  { value: 'sql', label: 'Sales Qualified' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'customer', label: 'Customer' }
];

const ContactsTab: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [items, setItems] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [showFullForm, setShowFullForm] = useState(false);
  
  // Quick add form state - essential fields only
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyId, setCompanyId] = useState('');
  const quickAddRef = useRef<HTMLInputElement | null>(null);

  const fetchCompanies = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data } = await companiesService.list({ organization_id: activeOrgId, limit: 100, offset: 0 });
      setCompanies(data || []);
    } catch (err: any) {
      console.error('Failed to load companies:', err);
    }
  }, [activeOrgId]);

  const fetchData = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const { data } = await contactsService.list({
        organization_id: activeOrgId,
        limit: 100,
        offset: 0,
        search: search.trim() || undefined,
        lifecycle_stage: lifecycleFilter || undefined
      });
      setItems(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, search, lifecycleFilter]);

  useEffect(() => {
    void fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const createContact = useCallback(async () => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    if (!firstName.trim() || !email.trim()) {
      toast.error('First name and email are required');
      return;
    }

    setLoading(true);
    try {
      const payload: ContactInput = {
        organization_id: activeOrgId,
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim(),
        mobile_phone: mobilePhone.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
        company_id: companyId || undefined
      };

      await contactsService.create(payload);
      setFirstName('');
      setLastName('');
      setEmail('');
      setMobilePhone('');
      setJobTitle('');
      setCompanyId('');
      toast.success('Contact added');
      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create contact');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, firstName, lastName, email, mobilePhone, jobTitle, companyId, fetchData]);

  const handleFullFormSubmit = useCallback(async (contactData: ContactInput) => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      await contactsService.create({ ...contactData, organization_id: activeOrgId });
      toast.success('Contact created successfully');
      setShowFullForm(false);
      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create contact');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, fetchData]);

  const groupedByLifecycle = useMemo(() => {
    const grouped: Record<string, Contact[]> = {};
    LIFECYCLE_OPTIONS.slice(1).forEach((opt) => {
      grouped[opt.value] = items.filter(item => item.lifecycle_stage === opt.value);
    });
    return grouped;
  }, [items]);

  return (
    <div className="space-y-6">
      {/* Filters & View Switcher */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <input
          type="text"
          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
        >
          {LIFECYCLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        
        {/* View Mode Switcher */}
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Table view"
          >
            📋
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'kanban' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Kanban view"
          >
            📊
          </button>
          <button
            onClick={() => setViewMode('gallery')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'gallery' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Gallery view"
          >
            🎴
          </button>
        </div>
        <Link
          to="/help?article=crm-contacts"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
        >
          ❓ Contacts help
        </Link>
      </div>

      {/* Quick Add Bar */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">➕ Quick Add Contact</h3>
          <button
            onClick={() => setShowFullForm(true)}
            className="text-sm font-semibold text-primary hover:underline"
          >
            + Open Full Form (Phone, Address, etc.)
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <input
            ref={quickAddRef}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="First name *"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={loading}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={loading}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Email *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="📱 Mobile Phone"
            type="tel"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.target.value)}
            disabled={loading}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Job Title"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            disabled={loading}
          />
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={loading}
          >
            <option value="">Select company</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          <div className="md:col-span-2 flex gap-2">
            <button
              onClick={() => void createContact()}
              disabled={loading}
              className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
            >
              Quick Add
            </button>
            <button
              onClick={() => setShowFullForm(true)}
              disabled={loading}
              className="rounded-lg border-2 border-primary px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/10 disabled:opacity-50"
            >
              Full Form
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">* Required fields. Use Full Form for business phone, extension, address, etc.</p>
      </div>

      {/* Views */}
      {viewMode === 'table' && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {search || lifecycleFilter ? 'No contacts match your filters.' : 'No contacts yet. Add one above!'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">📱 Mobile</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">☎️ Business</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Job Title</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Stage</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((contact) => (
                    <tr key={contact.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-primary">
                          {contact.first_name} {contact.last_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{contact.email}</td>
                      <td className="px-4 py-3 text-gray-600">{contact.mobile_phone || contact.phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {contact.business_phone ? (
                          <span>
                            {contact.business_phone}
                            {contact.business_phone_ext && <span className="text-xs text-gray-500"> x{contact.business_phone_ext}</span>}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{contact.job_title || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {companies.find(c => c.id === contact.company_id)?.name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                          {LIFECYCLE_OPTIONS.find(s => s.value === contact.lifecycle_stage)?.label || 'Lead'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                            className="rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/crm/deals?contactId=${contact.id}`)}
                            title="Create deal for this contact"
                            className="rounded-lg border border-green-600/30 px-3 py-1.5 text-xs font-semibold text-green-600 transition hover:bg-green-50"
                          >
                            + Deal
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'kanban' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
          {LIFECYCLE_OPTIONS.slice(1).map((stageOpt) => (
            <div key={stageOpt.value} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <h4 className="mb-3 text-sm font-semibold text-gray-700">{stageOpt.label}</h4>
              <div className="space-y-2">
                {groupedByLifecycle[stageOpt.value]?.map((contact) => (
                  <div
                    key={contact.id}
                    className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 transition hover:shadow-md"
                    onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                  >
                    <div className="font-medium text-gray-900">{contact.first_name} {contact.last_name}</div>
                    <div className="text-xs text-gray-500">{contact.email}</div>
                  </div>
                ))}
                {groupedByLifecycle[stageOpt.value]?.length === 0 && (
                  <div className="py-4 text-center text-xs text-gray-400">No contacts</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'gallery' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((contact) => (
            <div
              key={contact.id}
              className="cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              onClick={() => navigate(`/crm/contacts/${contact.id}`)}
            >
              <div className="mb-2 text-2xl">👤</div>
              <h4 className="mb-1 font-semibold text-gray-900">{contact.first_name} {contact.last_name}</h4>
              <p className="mb-2 text-xs text-gray-500">{contact.email}</p>
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                {LIFECYCLE_OPTIONS.find(s => s.value === contact.lifecycle_stage)?.label || 'Lead'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Full Form Modal */}
      {showFullForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add New Contact - Full Details</h2>
              <button
                onClick={() => setShowFullForm(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <EnhancedContactForm
                companies={companies}
                onSubmit={handleFullFormSubmit}
                onCancel={() => setShowFullForm(false)}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
